import React from 'react'
import { EuiButtonIcon, EuiFieldNumber, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiIcon, EuiText } from '@equipmentshare/ds2'
import { DeleteBin7LineIcon } from '@equipmentshare/ds2-icons'

import PartPicker from '../PartPicker'
import StorePartPicker from '../StorePartPicker'

import { TransactionItemFormState, PartDisplayInfo } from '../AddPartsForm'
import { StorePart, Part } from 'graphql'
import CurrencyField from 'components/CurrencyField/currencyField'
import { validateQuantity, checkForUnexpectedCost, SetUnexpectedCost } from '../../helpers/validateForm'
import useAssociatedStorePart from '../../helpers/useAssociatedStorePart'

export type PartRowProps = {
	isDisabled?: boolean,
	item: TransactionItemFormState,
	setItem: ( item: TransactionItemFormState ) => void,
	removeItem: () => void,
	storeId?: number,
	toId?: number,
	setUnexpectedCost: SetUnexpectedCost,
	onMarkUnavailable?: ( idx: number, productId: number ) => void,
}

export default function PartRow( { item, setItem, removeItem, isDisabled, storeId, toId, setUnexpectedCost, onMarkUnavailable }: PartRowProps ) {
	const { storePart: associatedStorePart, loading: isAssociatedStorePartLoading } = useAssociatedStorePart( toId, item.part?.id )

	const handleMarkUnavailable = ( productId: number ) => {
		if ( onMarkUnavailable ) {
			onMarkUnavailable( item.idx, productId )
		}
	}
	const onChangeQuantity = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const quantity = event.currentTarget.value
		setItem( {
			...item,
			quantity,
		} )
	}

	const onChangeCost = ( costPerItem: string, triggerValidation?: boolean ) => {
		if ( costPerItem !== item.costPerItem ) {
			const newItem = {
				...item,
				costPerItem,
			}

			if ( triggerValidation ) {
				newItem.validation = {
					...item.validation,
					costPerItem: { isInvalid: false }
				}
			}
			setItem( newItem )
		}
	}

	const onBlurQuantity = () => {
		setItem( {
			...item,
			validation: {
				...item.validation,
				quantity: validateQuantity( item.quantity, item.storePart?.availableQuantity )
			}
		} )
	}

	const onBlurCost = () => {
		const costValidation = checkForUnexpectedCost( item, associatedStorePart, setUnexpectedCost )
		setItem( {
			...item,
			validation: {
				...item.validation,
				costPerItem: costValidation
			}
		} )
	}

	const onSelectPart = ( _?: number, part?: Part ) => {
		setItem( {
			...item,
			part,
			storePart: undefined,
			costPerItem: '',
			validation: {
				...item.validation,
				part: { isInvalid: false },
				costPerItem: { isInvalid: false },
			}
		} )
	}

	const onSelectStorePart = ( _?: number, storePart?: StorePart ) => {
		const costPerItem = storePart?.weightedAverageCost?.amount?.toString() ?? ''
		setItem( {
			...item,
			storePart,
			part: undefined,
			costPerItem,
			validation: {
				...item.validation,
				costPerItem: { isInvalid: false },
				part: { isInvalid: false },
			}
		} )
	}

	const { part, storePart, costPerItem, quantity, validation, isUnavailable, selectedProductId } = item

	return (
		<EuiFlexGroup style={{ maxWidth: 700 }}>
			<EuiFlexItem>
				<EuiFormRow
					label="Item"
					style={{ width: 370 }}
					isInvalid={validation.part.isInvalid}
					error={validation.part.error}
					helpText={
						isUnavailable && part ? (
							<EuiText color="danger" size="xs">
								<EuiIcon type="alert" /> This part is not in stock at the fulfilling inventory
							</EuiText>
						) : undefined
					}
				>
					{ isUnavailable && part ? (
						<EuiFieldText
							value={`${part.partNumber} - ${part.partClass.description}`}
							disabled
							fullWidth
						/>
					) : storeId ? (
						<StorePartPicker
							isDisabled={isDisabled}
							selectedPart={storePart}
							selectedProductId={selectedProductId}
							onChange={onSelectStorePart}
							onNotFound={handleMarkUnavailable}
							storeId={storeId}
							required
							isInvalid={validation.part.isInvalid}
						/>
					) : (
						<PartPicker
							isDisabled={isDisabled}
							selectedPart={part}
							onChange={onSelectPart}
							required
							isInvalid={validation.part.isInvalid}
						/>
					) }

				</EuiFormRow>
			</EuiFlexItem>
			<EuiFlexItem grow={false} style={{ maxWidth: 75 }}>
				<EuiFormRow
					label="Qty"
					isInvalid={validation.quantity.isInvalid}
					error={validation.quantity.error}
				>
					<EuiFieldNumber
						min={1}
						disabled={isDisabled || isUnavailable}
						id="quantity"
						onChange={onChangeQuantity}
						placeholder={( storePart?.availableQuantity )?.toString()}
						step={1}
						value={quantity}
						onBlur={onBlurQuantity}
						isLoading={item.part && isAssociatedStorePartLoading}
						isInvalid={validation.quantity.isInvalid}
					/>
				</EuiFormRow>
			</EuiFlexItem>
			<EuiFlexItem>
				<EuiFormRow
					label="Price (per unit)"
					isInvalid={validation.costPerItem.isInvalid}
					error={validation.costPerItem.error}
				>
					<CurrencyField
						intlNumberFormatOptions={{ maximumFractionDigits: 4 }}
						disabled={isDisabled || isUnavailable}
						id="costPerItem"
						onRawChange={onChangeCost}
						value={costPerItem}
						onBlur={onBlurCost}
						isLoading={item.part && isAssociatedStorePartLoading}
						isInvalid={validation.costPerItem.isInvalid}
					/>
				</EuiFormRow>
			</EuiFlexItem>
			<EuiFlexItem grow={false}>
				<EuiFormRow hasEmptyLabelSpace>
					<EuiButtonIcon
						aria-label="Remove"
						isDisabled={isDisabled}
						iconType={DeleteBin7LineIcon}
						onClick={removeItem}
						color="danger"
					/>
				</EuiFormRow>
			</EuiFlexItem>
		</EuiFlexGroup>
	)
}
